import { useEffect, useRef, useState } from "react";
import SectionLabel from "./SectionLabel";

const HEADLINE_WORDS: { text: string; highlight?: boolean }[] = [
  { text: "Strawberry", highlight: true },
  { text: "Group", highlight: true },
  { text: "is" },
  { text: "a" },
  { text: "family-owned" },
  { text: "investment" },
  { text: "company," },
  { text: "with" },
  { text: "a" },
  { text: "portfolio" },
  { text: "consisting" },
  { text: "of" },
  { text: "real" },
  { text: "estate," },
  { text: "asset" },
  { text: "management," },
  { text: "art" },
  { text: "and" },
  { text: "design," },
  { text: "as" },
  { text: "well" },
  { text: "as" },
  { text: "being" },
  { text: "one" },
  { text: "of" },
  { text: "the" },
  { text: "leading" },
  { text: "hotel" },
  { text: "operators" },
  { text: "in" },
  { text: "the" },
  { text: "Nordics." },
  { text: "The" },
  { text: "company" },
  { text: "was" },
  { text: "founded" },
  { text: "by" },
  { text: "Petter", highlight: true },
  { text: "Anker", highlight: true },
  { text: "Stordalen.", highlight: true },
];

const TICKER_ITEMS = [
  "Real Estate",
  "Hotels",
  "Asset Management",
  "Art & Design",
  "Nordics",
  "Family Owned",
  "Since 1996",
];

export default function IntroSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const [counter, setCounter] = useState(0);

  // Trigger animations when section enters viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animated counter (1996 → present-ish)
  useEffect(() => {
    if (!inView) return;
    const start = 0;
    const end = 30;
    const dur = 1600;
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setCounter(Math.round(start + (end - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  // Mouse parallax
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMouse({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    });
  };

  const parallax = (strength: number) => ({
    transform: `translate3d(${(mouse.x - 0.5) * strength}px, ${
      (mouse.y - 0.5) * strength
    }px, 0)`,
  });

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative w-full overflow-hidden"
      style={{
        background: "#000000",
        padding: "160px 40px 140px",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      {/* animated grid backdrop */}
      <div className="intro-grid-bg" />

      {/* floating soft orbs (mouse parallax) */}
      <div
        className="intro-orb"
        style={{
          width: 520,
          height: 520,
          background: "#2563EB",
          top: -180,
          left: -160,
          opacity: 0.35,
          animation: "introFloat 14s ease-in-out infinite",
          ...parallax(40),
        }}
      />
      <div
        className="intro-orb"
        style={{
          width: 380,
          height: 380,
          background: "#60A5FA",
          bottom: -140,
          right: -100,
          opacity: 0.28,
          animation: "introFloatB 18s ease-in-out infinite",
          ...parallax(-30),
        }}
      />
      <div
        className="intro-orb"
        style={{
          width: 240,
          height: 240,
          background: "#93C5FD",
          top: "40%",
          right: "12%",
          opacity: 0.12,
          animation: "introFloat 22s ease-in-out infinite reverse",
          ...parallax(60),
        }}
      />

      {/* rotating dashed rings */}
      <div
        className="intro-ring"
        style={{
          width: 640,
          height: 640,
          top: "10%",
          left: "-8%",
          ...parallax(20),
        }}
      />
      <div
        className="intro-ring"
        style={{
          width: 320,
          height: 320,
          bottom: "8%",
          right: "6%",
          animationDuration: "60s",
          animationDirection: "reverse",
          ...parallax(-15),
        }}
      />

      {/* cursor spotlight */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `radial-gradient(600px circle at ${mouse.x * 100}% ${
            mouse.y * 100
          }%, rgba(37,99,235,0.14), transparent 60%)`,
          transition: "background 0.15s ease-out",
        }}
      />

      <div
        className="relative mx-auto grid gap-10"
        style={{
          maxWidth: 1440,
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        }}
      >
        {/* Left column */}
        <div
          style={{ gridColumn: "span 3 / span 3" }}
          className="flex flex-col justify-between gap-10"
        >
          <div
            className={`intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.05s" }}
          >
            <div className="flex items-center gap-3">
              <span
                className="inline-block rounded-full intro-label-dot"
                style={{ width: 8, height: 8, background: "#3B82F6" }}
              />
              <SectionLabel>What is Strawberry Group ?</SectionLabel>
            </div>
          </div>

          {/* counter badge */}
          <div
            className={`intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.55s" }}
          >
            <div
              className="relative rounded-2xl"
              style={{
                border: "1px solid rgba(255,255,255,0.09)",
                background:
                  "linear-gradient(160deg, rgba(255,255,255,0.04), rgba(37,99,235,0.08))",
                padding: "22px 20px",
                backdropFilter: "blur(6px)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#8A8A8A",
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Since 1996 · ~
                <span className="intro-counter" style={{ color: "#93C5FD" }}>
                  {counter}
                </span>{" "}
                years
              </div>
              <div
                style={{
                  fontSize: 15,
                  lineHeight: 1.5,
                  color: "#C8C8C8",
                }}
              >
                Building a Nordic universe of hotels, real estate, art &amp;
                design — one bold bet at a time.
              </div>
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  right: 14,
                  top: 14,
                  width: 10,
                  height: 10,
                  borderRadius: 9999,
                  background: "#3B82F6",
                  animation: "introDotPulse 2.4s ease-in-out infinite",
                }}
              />
            </div>
          </div>

          {/* vertical ticker */}
          <div
            className={`intro-fade-up ${inView ? "is-in" : ""}`}
            style={{ animationDelay: "0.75s" }}
          >
            <div
              style={{
                fontSize: 11,
                color: "#8A8A8A",
                letterSpacing: 0.5,
                textTransform: "uppercase",
                marginBottom: 10,
              }}
            >
              We invest in
            </div>
            <div
              style={{
                position: "relative",
                height: 24,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  animation: "verticalTicker 12s cubic-bezier(0.7, 0, 0.3, 1) infinite",
                }}
              >
                {TICKER_ITEMS.concat(TICKER_ITEMS[0]).map((t, i) => (
                  <div
                    key={i}
                    style={{
                      height: 24,
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#93C5FD",
                      letterSpacing: -0.2,
                    }}
                  >
                    {t}
                  </div>
                ))}
              </div>
              <style>{`
                @keyframes verticalTicker {
                  0%, 12% { transform: translateY(0); }
                  14%, 26% { transform: translateY(-24px); }
                  28%, 40% { transform: translateY(-48px); }
                  42%, 54% { transform: translateY(-72px); }
                  56%, 68% { transform: translateY(-96px); }
                  70%, 82% { transform: translateY(-120px); }
                  84%, 96% { transform: translateY(-144px); }
                  100% { transform: translateY(-168px); }
                }
              `}</style>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ gridColumn: "span 9 / span 9" }}>
          <p
            style={{
              fontSize: "clamp(30px, 3.6vw, 54px)",
              lineHeight: 1.12,
              letterSpacing: "-0.02em",
              margin: 0,
              fontWeight: 500,
              perspective: 1200,
            }}
          >
            {HEADLINE_WORDS.map((w, i) => (
              <span
                key={i}
                className={`intro-word ${inView ? "is-in" : ""} ${
                  w.highlight ? "is-highlight" : ""
                }`}
                style={{
                  color: w.highlight ? "#ECECEC" : "#8A8A8A",
                  marginRight: "0.28em",
                  animationDelay: `${0.2 + i * 0.035}s`,
                }}
              >
                {w.text}
              </span>
            ))}
          </p>

          {/* buttons */}
          <div
            className={`mt-10 flex flex-wrap items-center gap-3 intro-fade-up ${
              inView ? "is-in" : ""
            }`}
            style={{ animationDelay: `${0.2 + HEADLINE_WORDS.length * 0.035 + 0.1}s` }}
          >
            <button
              type="button"
              className="whitespace-nowrap cursor-pointer intro-btn"
              style={{
                background: "#E9E9E9",
                color: "#000",
                border: "none",
                borderRadius: 9999,
                padding: "13px 26px",
                fontWeight: 600,
                fontSize: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span>Explore our universe</span>
              <span
                className="intro-arrow"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                }}
              >
                <i className="ri-arrow-right-line" style={{ fontSize: 16 }} />
              </span>
              <span className="intro-btn__sweep" />
            </button>

            <button
              type="button"
              className="whitespace-nowrap cursor-pointer intro-btn intro-btn--ghost"
              style={{
                background: "transparent",
                color: "#ECECEC",
                border: "1px solid rgba(255,255,255,0.55)",
                borderRadius: 9999,
                padding: "12px 24px",
                fontWeight: 600,
                fontSize: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 9999,
                  background: "#3B82F6",
                  display: "inline-block",
                  animation: "introDotPulse 2s ease-in-out infinite",
                }}
              />
              <span>Meet Petter Stordalen</span>
              <span className="intro-btn__sweep" />
            </button>

            {/* scroll hint */}
            <div
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                color: "#8A8A8A",
                fontSize: 12,
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              <span>Scroll to explore</span>
              <span
                style={{
                  width: 28,
                  height: 44,
                  borderRadius: 9999,
                  border: "1px solid rgba(255,255,255,0.3)",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 6,
                    left: "50%",
                    width: 4,
                    height: 8,
                    borderRadius: 9999,
                    background: "#3B82F6",
                    transform: "translateX(-50%)",
                    animation: "scrollDot 1.8s ease-in-out infinite",
                  }}
                />
                <style>{`
                  @keyframes scrollDot {
                    0%, 100% { transform: translate(-50%, 0); opacity: 1; }
                    50% { transform: translate(-50%, 18px); opacity: 0.2; }
                  }
                `}</style>
              </span>
            </div>
          </div>

          {/* mini stats row */}
          <div
            className={`mt-16 grid grid-cols-4 gap-6 intro-fade-up ${
              inView ? "is-in" : ""
            }`}
            style={{
              animationDelay: `${0.2 + HEADLINE_WORDS.length * 0.035 + 0.25}s`,
            }}
          >
            {[
              { k: "43", l: "Portfolio companies" },
              { k: "250+", l: "Hotels" },
              { k: "21", l: "Countries" },
              { k: "30 000+", l: "People" },
            ].map((s, i) => (
              <div
                key={s.k}
                className="group relative"
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.12)",
                  paddingTop: 18,
                  transition: "border-color 0.4s ease",
                }}
              >
                <div
                  style={{
                    fontSize: 44,
                    fontWeight: 500,
                    color: "#ECECEC",
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                    transition: "color 0.35s ease, transform 0.35s ease",
                  }}
                  className="group-hover:!text-[#3B82F6] group-hover:!scale-105"
                >
                  {s.k}
                </div>
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: "#8A8A8A",
                  }}
                >
                  {s.l}
                </div>
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: -1,
                    left: 0,
                    height: 1,
                    width: "0%",
                    background: "#3B82F6",
                    transition: "width 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)",
                  }}
                  className="group-hover:!w-full"
                />
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 0,
                    width: 6,
                    height: 6,
                    borderRadius: 9999,
                    background: "#3B82F6",
                    opacity: 0,
                    transform: `translateX(0)`,
                    transition: "opacity 0.3s ease, transform 0.4s ease",
                    animationDelay: `${i * 0.1}s`,
                  }}
                  className="group-hover:!opacity-100 group-hover:!translate-x-1"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}